import { useQuery, DocumentNode } from '@apollo/client';
import React, { ReactElement, useContext, useMemo } from 'react';
import { ContextMenuTrigger } from 'react-contextmenu';
import DiaryEventWorkspace from '../../views/diary/DiaryEventWorkspace';
import DashboardCard from './DashboardCard';
import CustomerWorkspace from '../../views/customer/CustomerWorkspace';
import SalesOrderWorkspace from '../../views/sales/SalesOrderWorkspace';
import { getView } from '../../utils/ViewHelper';
import * as entities from '../../graphql/entities';
import SupplierWorkspace from '../../views/supplier/SupplierWorkspace';
import SalesInvoiceWorkspace from '../../views/salesLedger/SalesInvoiceWorkspace';
import SalesInvoiceLineWorkspace from '../../views/salesLedger/SalesInvoiceLineWorkspace';
import SalesReceiptWorkspace from '../../views/salesLedger/SalesReceiptWorkspace';
import NominalWorkspace from '../../views/nominal/NominalWorkspace';
import NominalTransactionWorkspace from '../../views/nominal/NominalTransactionWorkspace';
import BankWorkspace from '../../views/bank/BankWorkspace';
import ProductWorkspace from '../../views/product/ProductWorkspace';
import PurchaseOrderWorkspace from '../../views/purchases/PurchaseOrderWorkspace';
import SalesOrderLineWorkspace from '../../views/sales/SalesOrderLineWorkspace';

import numeral from 'numeral';
import ViewQuery from '../../graphql/queries/view/ViewQuery.graphql';
import '../../styles/partials/loading.scss';
import SalesCreditWorkspace from '../../views/salesLedger/SalesCreditWorkspace';
import SalesCreditLineWorkspace from '../../views/salesLedger/SalesCreditLineWorkspace';
import SalesRefundWorkspace from '../../views/salesLedger/SalesRefundWorkspace';
import SalesBadDebtWorkspace from '../../views/salesLedger/SalesBadDebtWorkspace';
import SalesTransactionWorkspace from '../../views/salesLedger/SalesTransactionWorkspace';
import ImportedAmazonOrderWorkspace from '../../views/sales/ImportedAmazonOrderWorkspace';
import ImportedEbayOrderWorkspace from '../../views/sales/ImportedEbayOrderWorkspace';
import ImportedShopifyOrderWorkspace from '../../views/sales/ImportedShopifyOrderWorkspace';
import ImportedWooCommerceOrderWorkspace from '../../views/sales/ImportedWooCommerceOrderWorkspace';
import NominalAccrualPrepaymentWorkspace from '../../views/nominal/NominalAccrualPrepaymentWorkspace';
import VatTransactionWorkspace from '../../views/vat/VatTransactionWorkspace';
import QuotationWorkspace from '../../views/sales/QuotationWorkspace';
import CombinedSalesWorkspace from '../../views/sales/CombinedSalesWorkspace';
import SalesInvoiceAnalysis, {
  ANALYTICS_REPORT_TYPES,
  IDimensionHistory,
  STANDARD_MEASURES,
} from '../../views/salesLedger/salesInvoiceAnalysis/SalesInvoiceAnalysis';
import Row from '../connectedForm/Row';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import { useSelector } from 'react-redux';
import {
  BLIND_COLOURS,
  COLOURS,
  OGL_COLOURS,
  RAINBOW_COLOURS,
  TRICOLOUR_BLIND,
  TRICOLOUR_STANDARD,
} from '../../styles/themes/default/widgetColours';
import {
  ColorScheme,
  Direction,
  IAggregateRow,
  IDashboardWidget,
  IDashboardWidgetDataset,
  IFilterInput,
  IFilterString,
  IFilterBigDecimal,
  IFilterLong,
  ISalesAnalyticsDimension,
  ISalesAnalyticsDimensionFilter,
  IView,
  IViewQueryResult,
  IViewQueryVariables,
  Maybe,
  Operation,
} from '../../graphql/schema/p4types';
import { IWorkspaceCommonInput, IWorkspaceView } from '../../@types/workspaceTypes';
import NivoPie, { NivoPieData } from './widgets/NivoPie';
import NivoBar from './widgets/NivoBar';
import NivoGauge from './widgets/NivoGauge';
import NivoProgress from './widgets/NivoProgress';
import NumberWidget from './widgets/NumberWidget';
import { useCompany } from '../../utils/FetchConfig';
import { WidgetWorkspace } from '../../@types/enums';
import ImportedPixSellOrderWorkspace from '../../views/sales/ImportedPixSellOrderWorkspace';
import PurchaseTransactionWorkspace from '../../views/supplier/PurchaseTransactionWorkspace';
import { ProductQueryWidget } from './ProductQueryWidget';
import PurchaseInvoiceWorkspace from '../../views/supplier/PurchaseInvoiceWorkspace';
import { getAvailableColumns } from '../view/availableColumnsUtil';

require('numeral/locales/en-gb');

numeral.locale('en-gb');
numeral.defaultFormat('$0,0.00');

const get2DimensionalPieData: (results: any, item: any) => Array<NivoPieData> = (results, item) => {
  let colours: Array<`#${string}`>;
  switch (item.colourScheme) {
    case ColorScheme.Rainbow:
      colours = RAINBOW_COLOURS;
      break;
    case ColorScheme.Blind:
      colours = BLIND_COLOURS;
      break;
    case ColorScheme.Standard:
      colours = COLOURS;
      break;
    default:
      colours = OGL_COLOURS;
  }

  return results && results.aggregateRows
    ? results.aggregateRows.map((row: IAggregateRow, index: number) => {
        let value;
        switch (item.operation) {
          case Operation.Sum:
            value = row.aggregateColumns?.[0]?.sum;
            break;
          case Operation.Average:
            value = row.aggregateColumns?.[0]?.sum / row.count;
            break;
          default:
            value = row.count;
        }

        return {
          id: row.groupColumns?.[0]?.value,
          label: row.groupColumns?.[0]?.value,
          value: value,
          color: colours[index % colours.length],
        };
      })
    : [];
};

const getTriColours = (item) => {
  return item.colourScheme === 'BLIND' ? TRICOLOUR_BLIND : TRICOLOUR_STANDARD;
};

// eslint-disable-next-line complexity
const getQuery: (item: any, view: IWorkspaceView) => { query?: DocumentNode; variables?: {} } = (item, view) => {
  if (item.type === 'WORKSPACE') return {};

  const variables: IWorkspaceCommonInput = {
    orderBy: view.orderBy,
    orderDirection: view.orderDirection,
    filters: view.filters || [],
    embeddedView: view.embeddedView,
    aggregateCriteria: {
      groupColumns: item.groupColumn ? [item.groupColumn] : null,
      sumColumns: [item.sumColumn || 'id'],
      columnOverrides: view.columns?.map((column) => ({
        fieldName: column.fieldName,
        totalCalculation: column.totalCalculation,
      })),
    },
  };

  let predefinedFilters: Maybe<Array<IFilterInput>>;
  let query: DocumentNode;
  switch (item.link || 'Diary/Task/Workspace') {
    case WidgetWorkspace.Customers:
      query = entities.Customer.aggregate!;
      break;
    case WidgetWorkspace.SalesOrders:
      predefinedFilters = entities.SalesOrder.workspacePredefinedFilters;
      query = entities.SalesOrder.aggregate!;
      break;
    case WidgetWorkspace.SalesQuotes:
      predefinedFilters = entities.SalesOrder.quoteWorkspacePredefinedFilters;
      query = entities.SalesOrder.aggregate!;
      break;
    case WidgetWorkspace.SalesOrdersAndQuotes:
      query = entities.SalesOrder.aggregate!;
      break;
    case WidgetWorkspace.Suppliers:
      query = entities.Supplier.aggregate!;
      break;
    case WidgetWorkspace.SalesInvoices:
      query = entities.SalesInvoice.aggregate!;
      break;
    case WidgetWorkspace.SalesInvoiceLines:
      query = entities.SalesInvoiceLine.aggregate!;
      break;
    case WidgetWorkspace.SalesReceipts:
      query = entities.SalesReceipt.aggregate!;
      break;
    case WidgetWorkspace.NominalAccounts:
      query = entities.Nominal.aggregate!;
      break;
    case WidgetWorkspace.NominalTransactions:
      query = entities.NominalTransaction.aggregate!;
      break;
    case WidgetWorkspace.BankAccounts:
      query = entities.Bank.aggregate!;
      break;
    case WidgetWorkspace.Products:
      query = entities.Product.aggregate!;
      break;
    case WidgetWorkspace.PurchaseOrders:
      query = entities.PurchaseOrder.aggregate!;
      break;
    case WidgetWorkspace.SalesOrderItems:
      query = entities.SalesOrderLine.aggregate!;
      break;
    case WidgetWorkspace.SalesCredits:
      query = entities.SalesCredit.aggregate!;
      break;
    case WidgetWorkspace.SalesCreditLines:
      query = entities.SalesCreditLine.aggregate!;
      break;
    case WidgetWorkspace.CustomerRefunds:
      query = entities.SalesRefund.aggregate!;
      break;
    case WidgetWorkspace.CustomerBadDebts:
      query = entities.SalesBadDebt.aggregate!;
      break;
    case WidgetWorkspace.SalesTransactions:
      query = entities.SalesTransaction.aggregate!;
      break;
    case WidgetWorkspace.ImportedAmazonOrders:
      query = entities.ImportedAmazonOrder.aggregate!;
      break;
    case WidgetWorkspace.ImportedEbayOrders:
      query = entities.ImportedEbayOrder.aggregate!;
      break;
    case WidgetWorkspace.ImportedShopifyOrders:
      query = entities.ImportedShopifyOrder.aggregate!;
      break;
    case WidgetWorkspace.ImportedWooCommerceOrders:
      query = entities.ImportedWooCommerceOrder.aggregate!;
      break;
    case WidgetWorkspace.NominalAccrualsAndPrepayments:
      query = entities.NominalAccrualPrepayment.aggregate!;
      break;
    case WidgetWorkspace.VatTransactions:
      query = entities.VatTransaction.aggregate!;
      break;
    case WidgetWorkspace.PurchaseTransactions:
      query = entities.PurchaseTransaction.aggregate!;
      break;
    case WidgetWorkspace.PurchaseInvoices:
      query = entities.PurchaseInvoice.aggregate!;
      break;
    case WidgetWorkspace.Tasks:
    default:
      query = entities.DiaryEvent.aggregate!;
      break;
  }

  if (predefinedFilters) {
    //Attach predefined filters to the view filters.
    variables.filters = [...predefinedFilters, ...variables.filters];
  }

  return { query: query, variables: variables };
};

export const useItemQuery = (item: IWidgetItem, viewProps: any, customColumns: any) => {
  const appUserId = useSelector((store: any) => store.app?.user?.id);
  const company = useCompany();

  const connectedForm = useContext(ConnectedFormContext);
  const dashboardOwnerId = connectedForm?.values?.companyUser?.systemUserId;

  const viewVariables = {
    systemUserId: dashboardOwnerId || appUserId || null, //If user id unavailable, filters won't be applied.
    screen: item.link,
    viewName: item.view,
  };

  const { data, loading } = useQuery<IViewQueryResult, IViewQueryVariables>(ViewQuery, { variables: viewVariables });

  /**
   * The UI filter handling does not handle "codes" and will not filter or display anything unless we map it to "objects".
   * (This is because it will attach additional data to the objects like 'reference', 'name', etc for displaying in filter dropdowns).
   */
  const amendedData = useMemo<IView | null>(() => {
    if (!data?.data) return null;
    const view = data.data as IView;

    let filterStrings: Array<IFilterString>;
    let filterBigDecimals: Array<IFilterBigDecimal>;
    let filterLongs: Array<IFilterLong>;
    if (customColumns) {
      filterStrings =
        view?.filterStrings?.map((fs) => {
          const matchingColumnIndex = Object.keys(customColumns).findIndex((key) => key === fs?.fieldName);
          if (matchingColumnIndex >= 0) {
            return { ...fs, fieldColumn: Object.values(customColumns)[matchingColumnIndex] } as IFilterString;
          }
          return fs as IFilterString;
        }) ?? [];
      filterBigDecimals =
        view?.filterBigDecimals?.map((fs) => {
          const matchingColumnIndex = Object.keys(customColumns).findIndex((key) => key === fs.fieldName);
          if (matchingColumnIndex >= 0) {
            return { ...fs, fieldColumn: Object.values(customColumns)[matchingColumnIndex] } as IFilterBigDecimal;
          }
          return fs as IFilterBigDecimal;
        }) ?? [];
      filterLongs =
        view?.filterBigDecimals?.map((fs) => {
          const matchingColumnIndex = Object.keys(customColumns).findIndex((key) => key === fs.fieldName);
          if (matchingColumnIndex >= 0) {
            return { ...fs, fieldColumn: Object.values(customColumns)[matchingColumnIndex] } as IFilterLong;
          }
          return fs as IFilterLong;
        }) ?? [];
    } else {
      filterStrings = (view?.filterStrings as Array<IFilterString>) ?? [];
      filterBigDecimals = (view?.filterBigDecimals as Array<IFilterBigDecimal>) ?? [];
      filterLongs = (view?.filterLongs as Array<IFilterLong>) ?? [];
    }

    const filterAttributeObjects =
      view.filterAttributeObjects?.map((fao) => {
        if (fao?.codes) {
          const objects = fao.codes.map((value) => ({ id: value }));
          return { ...fao, objects };
        }
        return { ...fao, objects: [] };
      }) ?? [];

    return { ...view, filterAttributeObjects, filterStrings, filterBigDecimals, filterLongs } as IView;
  }, [customColumns, data?.data]);

  const view = getView(
    viewProps,
    item.link || 'Diary/Task/Workspace',
    [],
    amendedData,
    company,
    getAvailableColumns(customColumns ?? {}, {})!
  );
  const queryProps = getQuery(item, view);

  const { data: widgetData } = useQuery(queryProps.query as DocumentNode, {
    variables: queryProps.variables,
    skip: !queryProps?.query || loading,
    pollInterval: item.interval || 1800000,
  });

  const results = widgetData?.listData;
  const totalColumns = results?.totalColumns;
  let value = 0;
  if (results && totalColumns?.[0]) {
    switch (item.operation) {
      case Operation.Sum:
        value = totalColumns[0].sum;
        break;
      case Operation.Average:
        value = totalColumns[0].sum / results.count;
        break;
      default:
        value = results.count;
    }
  }

  return { queryProps, results, value };
};

export type IQueryWidgetProps = {
  item: IWidgetItem;
  itemSecondary: IWidgetItem;
  gridMetrics: { rowHeight: number; cardWidth: number };
  viewProps: any;
};

const QueryWidget = ({ item, itemSecondary, gridMetrics: { rowHeight, cardWidth }, viewProps }: IQueryWidgetProps) => {
  const { queryProps, results, value } = useItemQuery(item, viewProps, null);
  const { value: value2 } = useItemQuery(itemSecondary, viewProps, null);
  return (
    <InnerQueryWidget
      item={item}
      rowHeight={rowHeight}
      cardWidth={cardWidth}
      queryProps={queryProps}
      results={results}
      value={value}
      value2={value2}
    />
  );
};

export const InnerQueryWidget = ({ item, rowHeight, cardWidth, queryProps, results, value, value2 }) => {
  if (!queryProps?.query) return <div>Could not load Widget data</div>;

  const { target, color, subTitle, valueMask } = item; //Note - Don't forget a 'color2' is available if anything would benefit from it.

  const cardPixelWidth = cardWidth * item.w;
  const cardPixelHeight = rowHeight * item.h;
  const cardPixelMin = cardPixelWidth < cardPixelHeight ? cardPixelWidth : cardPixelHeight;

  switch (item.type) {
    case 'PROGRESS':
      return (
        <NivoProgress
          value={value}
          target={target}
          foreColour={color as `#${string}`}
          graphColours={getTriColours(item)}
          valueMask={valueMask}
          subTitle={subTitle}
        />
      );
    case 'GAUGE':
      return (
        <NivoGauge
          value={value}
          target={item.target}
          subTitle={item.subTitle}
          height={cardPixelHeight - 30}
          semi={true}
          graphColours={getTriColours(item)}
          valueMask={valueMask}
          color={item.color2 as `#${string}`}
          foreColour={item.color as `#${string}`}
        />
      );
    case 'GAUGECIRCLE':
      return (
        <NivoGauge
          value={value}
          target={item.target}
          subTitle={item.subTitle}
          height={cardPixelHeight - 30}
          graphColours={getTriColours(item)}
          valueMask={valueMask}
          color={item.color2 as `#${string}`}
          foreColour={item.color as `#${string}`}
        />
      );
    case 'DOUGHNUT':
    case 'PIE':
      //Since a pie/doughnut is always equal width/height, figure out the 'excess' on the card & add margins appropriately to fit labels better.
      const widthExcess = cardPixelWidth > cardPixelMin ? cardPixelWidth - cardPixelMin : 0;
      const heightExcess = cardPixelHeight > cardPixelMin ? cardPixelHeight - cardPixelMin : 0;

      return (
        <div
          style={{
            height: `${cardPixelHeight}px`,
            width: `${cardPixelWidth}px`,
          }}
        >
          <NivoPie
            type={item.type}
            data={get2DimensionalPieData(results, item)}
            margin={{
              top: 40 + heightExcess / 2,
              right: 80 + widthExcess / 2,
              bottom: 80 + heightExcess / 2,
              left: 80 + widthExcess / 2,
            }}
          />
        </div>
      );
    case 'BAR':
      return (
        <div
          style={{
            height: `${cardPixelHeight}px`,
            width: `${cardPixelWidth}px`,
          }}
        >
          <NivoBar
            data={get2DimensionalPieData(results, item)}
            cardPixelWidth={cardPixelWidth}
            labelTextColor={color}
          />
        </div>
      );
    //TODO - Should obey 'valueMask' for the new Nivo components.
    // return <BarChart {...item} size={size} data={getOld2DimensionalData(results, item)} valueMask={valueMask} />;
    case 'NUMBER':
      let numberValue = value;
      if (item.calculation === 'DIVIDE') {
        numberValue /= value2;
      } else if (item.calculation === 'SUM') {
        numberValue += value2;
      }

      return <NumberWidget {...item} value={numberValue} valueMask={item.valueMask} color={item.color} />;
    default:
      return <div>No chart type is defined</div>;
  }
};

export type IWidgetItem = Omit<
  IDashboardWidget & IDashboardWidgetDataset,
  'mainDataset' | 'secondaryDataset' | '__typename'
>;

type IQWidgetProps = {
  viewScreenName?: string;
  location?: any;
  predefinedFilters?: Array<IFilterInput>;
  embeddedView?: boolean;
  views?: any;
  altView?: any;
  additionalVariables?: Object;
};

export type IWidgetProps = {
  gridMetrics: { rowHeight: number; cardWidth: number; gridWidth?: number };
  handleDrillFilter: (options: IDimensionHistory) => void;
  idx?: number;
  value: IDashboardWidget;
  connectedForm?: any;
  dataid: string;
  'data-testid'?: string;
} & IQWidgetProps;

type IReformattedHistory = Maybe<{
  dimension: Maybe<ISalesAnalyticsDimension>;
  dimensionFilters: Array<{ dimension: Maybe<string>; id: number }>;
  history: Array<IReformattedHistory>;
}>;

// eslint-disable-next-line complexity
const Widget = (props: IWidgetProps) => {
  const {
    gridMetrics,
    handleDrillFilter,
    idx,
    value,
    connectedForm,
    dataid,
    viewScreenName,
    location,
    predefinedFilters,
    embeddedView,
    views,
    altView,
    additionalVariables,
  } = props;

  let component: ReactElement | null = null;
  const { mainDataset, secondaryDataset, ...rest } = value;

  //Merge main dataset into rest for display purposes.
  const item: IWidgetItem = { ...rest, ...mainDataset };
  const itemSecondary = { ...rest, ...secondaryDataset };

  if (item.deleted) {
    return <div>This widget will be removed on save.</div>;
  }

  const { rowHeight, cardWidth } = gridMetrics;
  const width = cardWidth * item.w;
  const height = rowHeight * item.h;

  if (item.type === 'WORKSPACE') {
    const workspaceMap: { [key in WidgetWorkspace]: any } = {
      [WidgetWorkspace.Tasks]: DiaryEventWorkspace,
      [WidgetWorkspace.Customers]: CustomerWorkspace,
      [WidgetWorkspace.Suppliers]: SupplierWorkspace,
      [WidgetWorkspace.SalesInvoices]: SalesInvoiceWorkspace,
      [WidgetWorkspace.SalesInvoiceLines]: SalesInvoiceLineWorkspace,
      [WidgetWorkspace.SalesReceipts]: SalesReceiptWorkspace,
      [WidgetWorkspace.NominalAccounts]: NominalWorkspace,
      [WidgetWorkspace.NominalTransactions]: NominalTransactionWorkspace,
      [WidgetWorkspace.BankAccounts]: BankWorkspace,
      [WidgetWorkspace.Products]: ProductWorkspace,
      [WidgetWorkspace.PurchaseOrders]: PurchaseOrderWorkspace,
      [WidgetWorkspace.SalesOrderItems]: SalesOrderLineWorkspace,
      [WidgetWorkspace.SalesOrders]: SalesOrderWorkspace,
      [WidgetWorkspace.SalesQuotes]: QuotationWorkspace,
      [WidgetWorkspace.SalesOrdersAndQuotes]: CombinedSalesWorkspace,
      [WidgetWorkspace.SalesCredits]: SalesCreditWorkspace,
      [WidgetWorkspace.SalesCreditLines]: SalesCreditLineWorkspace,
      [WidgetWorkspace.CustomerRefunds]: SalesRefundWorkspace,
      [WidgetWorkspace.CustomerBadDebts]: SalesBadDebtWorkspace,
      [WidgetWorkspace.SalesTransactions]: SalesTransactionWorkspace,
      [WidgetWorkspace.ImportedAmazonOrders]: ImportedAmazonOrderWorkspace,
      [WidgetWorkspace.ImportedEbayOrders]: ImportedEbayOrderWorkspace,
      [WidgetWorkspace.ImportedShopifyOrders]: ImportedShopifyOrderWorkspace,
      [WidgetWorkspace.ImportedWooCommerceOrders]: ImportedWooCommerceOrderWorkspace,
      [WidgetWorkspace.ImportedPixSellOrders]: ImportedPixSellOrderWorkspace,
      [WidgetWorkspace.NominalAccrualsAndPrepayments]: NominalAccrualPrepaymentWorkspace,
      [WidgetWorkspace.VatTransactions]: VatTransactionWorkspace,
      [WidgetWorkspace.PurchaseTransactions]: PurchaseTransactionWorkspace,
      [WidgetWorkspace.PurchaseInvoices]: PurchaseInvoiceWorkspace,
    };

    const WorkspaceComponent = (item.link && workspaceMap[item.link]) || DiaryEventWorkspace; //Diary just an arbitrary default. (Could remove this?)

    if (WorkspaceComponent) {
      component = (
        <WorkspaceComponent
          miniGrid={true}
          contentHeight={height - 25}
          contentWidth={width - 45}
          location={{ pathname: `/${item.link}` }}
          match={{ path: '/', url: '/' }}
          dashboard={['id']}
          altView={item.view}
          altUserId={connectedForm?.values?.companyUser?.systemUserId}
          viewScreenName={item.link}
        />
      );
    } else {
      component = <div>Workspace component is not available.</div>;
    }
  } else if (item.type === 'ANALYTICS') {
    let colours;
    switch (item.colourScheme) {
      case ColorScheme.Rainbow:
        colours = RAINBOW_COLOURS;
        break;
      case ColorScheme.Blind:
        colours = BLIND_COLOURS;
        break;
      case ColorScheme.Standard:
        colours = COLOURS;
        break;
      default:
        colours = OGL_COLOURS;
    }

    let historyArray = item.salesAnalyticsHistory;

    const isDimensionFilter = (
      dimensionFilter: Maybe<ISalesAnalyticsDimensionFilter>
    ): dimensionFilter is ISalesAnalyticsDimensionFilter => !!dimensionFilter;

    let reformattedHistory: IReformattedHistory;
    if (historyArray) {
      historyArray = historyArray.filter((entry) => !entry?.deleted);
      for (let i = 0; i < historyArray.length; i++) {
        const history = !reformattedHistory ? [] : [...reformattedHistory.history, reformattedHistory];

        reformattedHistory = {
          dimension: historyArray[i]?.dimension,
          dimensionFilters:
            historyArray[i]?.dimensionFilters?.filter(isDimensionFilter).map((dimensionFilter) => ({
              dimension: dimensionFilter.dimension,
              id: dimensionFilter.dimensionId,
            })) || [],
          history: history,
        };
      }
    }

    component = (
      <Row>
        <SalesInvoiceAnalysis
          dataid={dataid}
          graphOnly={true}
          graphHeight={height}
          reportType={ANALYTICS_REPORT_TYPES.find((a) => a.value === item.reportType) || undefined}
          dimension={item.dimension ? { value: item.dimension } : undefined}
          measure={STANDARD_MEASURES.find((a) => a.value === item.measure) || undefined}
          graphType={item.graphType}
          recordsInGraph={item.recordsInGraph}
          sortColumn={'difference'}
          sortDirection={item.sortDirection || Direction.Desc}
          colorScheme={colours}
          handleDrillFilter={handleDrillFilter}
          history={reformattedHistory as IDimensionHistory}
        />
      </Row>
    );
  } else if (item.link === 'Products/Product/Workspace') {
    component = (
      <ProductQueryWidget
        item={item}
        itemSecondary={itemSecondary}
        gridMetrics={gridMetrics}
        viewProps={{
          viewScreenName,
          location,
          predefinedFilters,
          embeddedView,
          views,
          altView,
          additionalVariables,
        }}
      />
    );
  } else {
    component = (
      <QueryWidget
        item={item}
        itemSecondary={itemSecondary}
        gridMetrics={gridMetrics}
        viewProps={{
          viewScreenName,
          location,
          predefinedFilters,
          embeddedView,
          views,
          altView,
          additionalVariables,
        }}
      />
    );
  }

  const className = item.type === 'WORKSPACE' ? 'miniGrid' : undefined;

  let labelColor: `#${string}`;
  let labelValue: string;
  if (item.label) {
    labelValue = item.label;
    labelColor = item.color as `#${string}`;
  } else {
    labelValue = '[No Label]';
    labelColor = '#cd342a';
  }

  const label: ReactElement = (
    <ContextMenuTrigger id={`widget_menu_${idx}`}>
      <div style={{ color: labelColor }}>{labelValue}</div>
    </ContextMenuTrigger>
  );

  const link = item.type === 'ANALYTICS' ? 'Customers/SalesAnalytics' : item.link;

  return (
    <div
      className={className}
      style={{
        height: '100%',
        width: '100%',
        backgroundColor: item.backgroundColor || undefined,
        position: 'relative',
      }}
    >
      <DashboardCard label={label} link={link} view={item.view} data-testid={props['data-testid']} item={item}>
        {component}
      </DashboardCard>
    </div>
  );
};

export default Widget;
